import { useEffect, useRef } from 'react';
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';
import { useAppStore } from '../state/store';
import type { EquityCurveResult } from '../state/types';
import { getCanonicalTimestampsForAllCharts, padSeriesToTimeline } from '../lib/chartTimeline';
import { applyRange, RANGE_PRESETS } from '../lib/chartRangePresets';
import type { ChartTimeSyncPreset } from '../state/store';
import {
  chartPanPlugin,
  CHART_RIGHT_AXIS_SIZE,
  createXAxisConfig,
  findIndexForTimestamp,
  getChartCursorSyncConfig,
  xAxisSecondRowPlugin,
} from './PrimarySeriesChart';
import { useSyncedCrosshair } from '../hooks/useSyncedCrosshair';

const CHART_HEIGHT = 100;
const BAR_WIDTH_FACTOR = 0.6;
const UNITS_FILL = '#3a4e6e';

function toUnixSeconds(ts: string): number {
  const s = ts.slice(0, 10);
  return Math.floor(new Date(s + 'T00:00:00Z').getTime() / 1000);
}

function formatLegendDate(timestamps: number[], idx: number): string {
  const i = Math.max(0, Math.min(timestamps.length - 1, Math.round(idx)));
  const sec = timestamps[i];
  if (sec == null) return '';
  const d = new Date(sec * 1000);
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`;
}

function formatUnits(rawVal: number): string {
  if (Number.isInteger(rawVal)) return String(rawVal);
  return rawVal.toFixed(2);
}

/**
 * uPlot plugin: bars from 0 to value per index. data[1] = units held.
 */
function unitsBarsPlugin() {
  function draw(u: uPlot) {
    u.ctx.save();
    const { width } = u.bbox;
    const units = u.data[1] as (number | null)[];
    if (!units) {
      u.ctx.restore();
      return;
    }
    const xMin = u.scales.x.min ?? 0;
    const xMax = u.scales.x.max ?? units.length - 1;
    const iMin = Math.max(0, Math.floor(xMin));
    const iMax = Math.min(units.length - 1, Math.ceil(xMax));
    const visibleBarCount = Math.max(1, iMax - iMin + 1);
    const pixelsPerBar = width / visibleBarCount;

    for (let i = iMin; i <= iMax; i++) {
      const val = units[i];
      if (val == null || !Number.isFinite(val) || val === 0) continue;

      const xVal = u.scales.x.distr === 2 ? i : (u.data[0] as number[])[i];
      const xCenter = u.valToPos(xVal, 'x', true);
      const barPxWidth = Math.max(1, Math.round(pixelsPerBar * BAR_WIDTH_FACTOR));
      const barLeft = Math.round(xCenter - barPxWidth / 2);

      const y0 = u.valToPos(0, 'y', true);
      const yVal = u.valToPos(val, 'y', true);
      const top = Math.min(y0, yVal);
      const bottom = Math.max(y0, yVal);

      u.ctx.fillStyle = UNITS_FILL;
      u.ctx.fillRect(barLeft, Math.round(top), barPxWidth, Math.round(bottom - top));
    }
    u.ctx.restore();
  }

  return {
    opts: (_u: uPlot, baseOpts: uPlot.Options) => {
      baseOpts.series.forEach((s, i) => {
        if (i > 0) {
          (s as uPlot.Series).paths = () => null;
          (s as uPlot.Series).points = { show: false };
        }
      });
    },
    hooks: { draw },
  };
}

function buildChartData(
  curve: EquityCurveResult,
  canonicalTimestamps: number[]
): { data: uPlot.AlignedData; timestamps: number[] } {
  const equityTimestamps = curve.timestamps.map(toUnixSeconds);
  const padded = padSeriesToTimeline(
    canonicalTimestamps,
    equityTimestamps,
    curve.units_held_per_bar,
    0
  );
  const n = canonicalTimestamps.length;
  const data: uPlot.AlignedData = [
    Array.from({ length: n }, (_, i) => i),
    padded,
  ];
  return { data, timestamps: canonicalTimestamps };
}

export default function TimeInMarketChart() {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<uPlot | null>(null);
  const timestampsRef = useRef<number[]>([]);
  const ohlc = useAppStore((s) => s.ohlc);
  const primaryInstrumentId = useAppStore((s) => s.primaryInstrumentId);
  const instruments = useAppStore((s) => s.instruments);
  const backtestEquityCurve = useAppStore((s) => s.backtestEquityCurve);
  const chartTimeSync = useAppStore((s) => s.chartTimeSync);
  const chartTimeSyncPreset = useAppStore((s) => s.chartTimeSyncPreset);
  const setChartTimeRange = useAppStore((s) => s.setChartTimeRange);
  const setChartVisibleTimeRange = useAppStore((s) => s.setChartVisibleTimeRange);
  const setChartTimeSyncPreset = useAppStore((s) => s.setChartTimeSyncPreset);
  const primaryChartFullWidth = useAppStore((s) => s.primaryChartFullWidth);

  useSyncedCrosshair(
    'timeInMarket',
    chartRef,
    timestampsRef,
    (ts) => findIndexForTimestamp(timestampsRef.current, ts ?? 0) ?? undefined
  );

  useEffect(() => {
    if (!containerRef.current || !backtestEquityCurve?.units_held_per_bar) return;

    const equityTs = backtestEquityCurve.timestamps.map(toUnixSeconds);
    const instrumentIds = instruments.map((i) => i.id);
    const canonicalTimestamps = getCanonicalTimestampsForAllCharts(
      ohlc,
      primaryInstrumentId,
      instrumentIds,
      equityTs.length > 0 ? equityTs : undefined
    );
    if (canonicalTimestamps.length === 0) return;

    const { data, timestamps } = buildChartData(backtestEquityCurve, canonicalTimestamps);
    timestampsRef.current = timestamps;
    if (data[0].length === 0) return;

    const n = data[0].length - 1;
    const sync = useAppStore.getState().chartTimeSync;
    let xMin = 0;
    let xMax = Math.max(0, n);
    if (sync.xMin != null && sync.xMax != null) {
      xMin = Math.max(0, Math.min(sync.xMin, n));
      xMax = Math.max(0, Math.min(sync.xMax, n));
      if (xMin >= xMax) {
        xMin = 0;
        xMax = n;
      }
    }

    const fullW = useAppStore.getState().primaryChartFullWidth;
    const width =
      fullW != null
        ? fullW
        : Math.max(containerRef.current.clientWidth, 100);
    const units = data[1] as (number | null)[];
    const finite = units.filter((v): v is number => v != null && Number.isFinite(v) && v > 0);
    const yMax = Math.max(1, ...finite, 1);
    const yMin = 0;

    const opts: uPlot.Options = {
      width,
      height: CHART_HEIGHT,
      plugins: [
        unitsBarsPlugin(),
        chartPanPlugin(
          (min, max) => useAppStore.getState().setChartTimeRange(min, max),
          undefined,
          getChartCursorSyncConfig('timeInMarket', (_u, idx) => timestamps[idx] ?? null)
        ),
        xAxisSecondRowPlugin(timestamps),
      ],
      series: [
        {
          label: 'Date',
          value: (_u, _rawValue, _seriesIdx, idx) =>
            idx != null ? formatLegendDate(timestamps, idx) : '--',
        },
        {
          label: 'Units held',
          scale: 'y',
          value: (_u, rawVal) =>
            rawVal != null && Number.isFinite(rawVal as number)
              ? formatUnits(rawVal as number)
              : '--',
        },
      ],
      scales: {
        x: { min: xMin, max: xMax, distr: 2 },
        y: { min: yMin, max: yMax, auto: false },
      },
      axes: [
        createXAxisConfig(timestamps),
        {
          scale: 'y',
          side: 1,
          size: CHART_RIGHT_AXIS_SIZE,
          values: (_u, vals) =>
            vals.map((v) => (v != null && Number.isFinite(v) ? formatUnits(v as number) : '')),
        },
      ],
    };

    const chart = new uPlot(opts, data, containerRef.current);
    chartRef.current = chart;

    const ro = new ResizeObserver(() => {
      if (!containerRef.current || !chartRef.current) return;
      const fullW = useAppStore.getState().primaryChartFullWidth;
      const w =
        fullW != null ? fullW : containerRef.current.clientWidth;
      chartRef.current.setSize({ width: w, height: CHART_HEIGHT });
    });
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      chart.destroy();
      chartRef.current = null;
    };
  }, [backtestEquityCurve, ohlc, primaryInstrumentId, instruments]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || primaryChartFullWidth == null) return;
    chart.setSize({
      width: primaryChartFullWidth,
      height: CHART_HEIGHT,
    });
  }, [primaryChartFullWidth]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || chartTimeSync.xMin == null || chartTimeSync.xMax == null) return;
    const n = (chart.data[0] as number[]).length - 1;
    const xMin = Math.max(0, Math.min(chartTimeSync.xMin, n));
    const xMax = Math.max(0, Math.min(chartTimeSync.xMax, n));
    if (xMin >= xMax) return;
    if (chart.scales.x.min === xMin && chart.scales.x.max === xMax) return;
    chart.setScale('x', { min: xMin, max: xMax });
  }, [chartTimeSync.xMin, chartTimeSync.xMax]);

  function handleRangeSelect(preset: ChartTimeSyncPreset) {
    const chart = chartRef.current;
    const timestamps = timestampsRef.current;
    if (!chart) {
      setChartTimeSyncPreset(preset);
      return;
    }
    const n = (chart.data[0] as number[]).length;
    if (n === 0) return;
    const rightEdgeIndex =
      chart.scales.x.min != null && chart.scales.x.max != null
        ? Math.max(0, Math.min(n - 1, Math.round(chart.scales.x.max)))
        : n - 1;
    const xRange = applyRange(chart, n, preset, rightEdgeIndex);
    setChartTimeRange(xRange.min, xRange.max);
    if (timestamps.length > 0) {
      setChartVisibleTimeRange(
        timestamps[Math.min(xRange.min, timestamps.length - 1)],
        timestamps[Math.min(xRange.max, timestamps.length - 1)]
      );
    }
    setChartTimeSyncPreset(preset);
  }

  const hasData =
    backtestEquityCurve?.units_held_per_bar &&
    backtestEquityCurve.timestamps.length > 0;
  const instrumentIds = instruments.map((i) => i.id);
  const equityTs = backtestEquityCurve?.timestamps.map(toUnixSeconds) ?? [];
  const canonicalTimestamps = hasData
    ? getCanonicalTimestampsForAllCharts(
        ohlc,
        primaryInstrumentId,
        instrumentIds,
        equityTs.length > 0 ? equityTs : undefined
      )
    : [];
  const showChart = hasData && canonicalTimestamps.length > 0;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold font-headline text-ink">Time in market</h3>
        <div className="flex items-center gap-1">
          {RANGE_PRESETS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => handleRangeSelect(id)}
              className={
                'px-2 py-1 text-xs rounded-sm border transition-colors ' +
                (chartTimeSyncPreset === id
                  ? 'bg-ink text-newsprint border-ink'
                  : 'bg-newsprint-panel text-ink border-rule hover:bg-newsprint-dark')
              }
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {showChart ? (
        <>
          <div
            ref={containerRef}
            className="w-full"
            style={{
              height: CHART_HEIGHT,
              ...(primaryChartFullWidth != null && { width: primaryChartFullWidth }),
            }}
          />
          {backtestEquityCurve && (
            <TimeInMarketStats curve={backtestEquityCurve} />
          )}
        </>
      ) : (
        <div className="flex items-center justify-center py-8 text-ink/70 text-sm">
          No Data
        </div>
      )}
    </div>
  );
}

function TimeInMarketStats({ curve }: { curve: EquityCurveResult }) {
  const units = curve.units_held_per_bar;
  const totalDays = curve.timestamps.length;
  const daysInMarket = units.filter((u) => u > 0).length;
  const pct =
    totalDays > 0 ? ((daysInMarket / totalDays) * 100).toFixed(1) : '0';

  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink/70">
      <span>
        # of days in market:{' '}
        <strong className="text-ink">
          {daysInMarket} out of {totalDays}
        </strong>
      </span>
      <span>
        % in market: <strong className="text-ink">{pct}%</strong>
      </span>
    </div>
  );
}
